import React, { useState } from 'react';
import { FaEdit, FaTrash, FaSave, FaTimes, FaEye, FaCalendar, FaUser, FaTag, FaMoneyBillWave } from 'react-icons/fa';
import Swal from 'sweetalert2';

const EntryList = ({ entries, deleteEntry, updateEntry, isAdmin, darkMode }) => {
  const [editingEntry, setEditingEntry] = useState(null);
  const [editFormData, setEditFormData] = useState({
    type: '',
    amount: '',
    description: '',
    category: '',
  });

  const categories = Array.from(new Set(entries.map((entry) => entry.category)));

  const getCategoryBgColor = (category) => {
    const colorMap = {
      School: 'bg-red-500',
      '14starsfarm': 'bg-blue-500',
      '5starsfarm': 'bg-green-500',
      Testing: 'bg-yellow-500',
    };
    return colorMap[category] || 'bg-gray-500';
  };

  const handleEditClick = async (entry) => {
    // Show confirmation modal before editing
    const result = await Swal.fire({
      title: 'Edit Transaction',
      text: `Are you sure you want to edit the transaction "${entry.description}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, edit it!',
      cancelButtonText: 'Cancel',
      background: darkMode ? '#1f2937' : '#ffffff',
      color: darkMode ? '#ffffff' : '#000000',
      customClass: {
        popup: darkMode ? 'dark-mode' : '',
        title: darkMode ? 'text-white' : 'text-gray-900',
        content: darkMode ? 'text-gray-300' : 'text-gray-600',
        confirmButton: 'bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700',
        cancelButton: 'bg-gray-500 hover:bg-gray-600'
      }
    });

    if (result.isConfirmed) {
      setEditingEntry(entry.id);
      setEditFormData({
        type: entry.type,
        amount: entry.amount,
        description: entry.description,
        category: entry.category,
      });
    }
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleEditFormSubmit = async (e) => {
    e.preventDefault();
    
    // Show confirmation modal before saving changes
    const result = await Swal.fire({
      title: 'Save Changes',
      text: 'Are you sure you want to save these changes?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, save changes!',
      cancelButtonText: 'Cancel',
      background: darkMode ? '#1f2937' : '#ffffff',
      color: darkMode ? '#ffffff' : '#000000',
      customClass: {
        popup: darkMode ? 'dark-mode' : '',
        title: darkMode ? 'text-white' : 'text-gray-900',
        content: darkMode ? 'text-gray-300' : 'text-gray-600',
        confirmButton: 'bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700',
        cancelButton: 'bg-gray-500 hover:bg-gray-600'
      }
    });

    if (result.isConfirmed) {
      try {
        await updateEntry(editingEntry, editFormData);
        setEditingEntry(null);
        
        // Show success message
        Swal.fire({
          title: 'Success!',
          text: 'Transaction updated successfully!',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#ffffff' : '#000000',
          customClass: {
            popup: darkMode ? 'dark-mode' : '',
            title: darkMode ? 'text-white' : 'text-gray-900',
            content: darkMode ? 'text-gray-300' : 'text-gray-600'
          }
        });
      } catch (error) {
        console.error('Error updating entry:', error);
        
        // Show error message
        Swal.fire({
          title: 'Error!',
          text: 'Failed to update transaction. Please try again.',
          icon: 'error',
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#ffffff' : '#000000',
          customClass: {
            popup: darkMode ? 'dark-mode' : '',
            title: darkMode ? 'text-white' : 'text-gray-900',
            content: darkMode ? 'text-gray-300' : 'text-gray-600'
          }
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditFormData({
      type: '',
      amount: '',
      description: '',
      category: '',
    });
  };

  const handleDeleteClick = async (entry) => {
    // Show confirmation modal before deleting
    const result = await Swal.fire({
      title: 'Delete Transaction',
      text: `Are you sure you want to delete the transaction "${entry.description}"? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      background: darkMode ? '#1f2937' : '#ffffff',
      color: darkMode ? '#ffffff' : '#000000',
      customClass: {
        popup: darkMode ? 'dark-mode' : '',
        title: darkMode ? 'text-white' : 'text-gray-900',
        content: darkMode ? 'text-gray-300' : 'text-gray-600',
        confirmButton: 'bg-red-600 hover:bg-red-700',
        cancelButton: 'bg-gray-500 hover:bg-gray-600'
      }
    });

    if (result.isConfirmed) {
      try {
        await deleteEntry(entry.id);
        
        // Show success message
        Swal.fire({
          title: 'Deleted!',
          text: 'Transaction has been deleted successfully.',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false,
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#ffffff' : '#000000',
          customClass: {
            popup: darkMode ? 'dark-mode' : '',
            title: darkMode ? 'text-white' : 'text-gray-900',
            content: darkMode ? 'text-gray-300' : 'text-gray-600'
          }
        });
      } catch (error) {
        console.error('Error deleting entry:', error);
        
        // Show error message
        Swal.fire({
          title: 'Error!',
          text: 'Failed to delete transaction. Please try again.',
          icon: 'error',
          background: darkMode ? '#1f2937' : '#ffffff',
          color: darkMode ? '#ffffff' : '#000000',
          customClass: {
            popup: darkMode ? 'dark-mode' : '',
            title: darkMode ? 'text-white' : 'text-gray-900',
            content: darkMode ? 'text-gray-300' : 'text-gray-600'
          }
        });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className={`overflow-hidden ${darkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-800'}`}>
      {entries.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-4">
            <div className="mx-auto h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-full flex items-center justify-center">
              <FaEye className="h-6 w-6 text-gray-400" />
            </div>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No transactions found</h3>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto text-sm">
            {entries.length === 0 ? 'No transactions match the selected filters' : 'Add your first transaction to get started'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className={`min-w-full shadow-lg rounded-xl overflow-hidden ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <thead className={`${darkMode ? 'bg-gray-700' : 'bg-gradient-to-r from-blue-600 to-teal-600'} text-white`}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  <div className="flex items-center">
                    <FaCalendar className="mr-1 h-3 w-3" />
                    Date & Time
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  <div className="flex items-center">
                    <FaTag className="mr-1 h-3 w-3" />
                    Category
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  <div className="flex items-center">
                    <FaMoneyBillWave className="mr-1 h-3 w-3" />
                    Type
                  </div>
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  Description
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider">
                  <div className="flex items-center">
                    <FaUser className="mr-1 h-3 w-3" />
                    Added By
                  </div>
                </th>
                {isAdmin && (
                  <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className={`transition duration-200 ease-in-out ${
                    darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                  } border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
                >
                  {editingEntry === entry.id ? (
                    <td colSpan={isAdmin ? 7 : 6}>
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-teal-50 dark:from-blue-900/20 dark:to-teal-900/20 rounded-lg m-3">
                        <div className="flex items-center mb-3">
                          <FaEdit className="h-4 w-4 text-blue-600 dark:text-blue-400 mr-2" />
                          <h4 className="font-semibold text-gray-900 dark:text-white text-sm">Edit Transaction</h4>
                        </div>
                        <form onSubmit={handleEditFormSubmit} className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                            {/* Read-only Date */}
                            <div className="flex items-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                              <FaCalendar className="h-3 w-3 text-gray-500 mr-2" />
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Date:</label>
                                <span className="text-xs font-medium text-gray-900 dark:text-white">{formatDate(entry.created_at)}</span>
                              </div>
                            </div>
                            {/* Read-only Added By */}
                            <div className="flex items-center p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                              <FaUser className="h-3 w-3 text-gray-500 mr-2" />
                              <div>
                                <label className="block text-xs font-medium text-gray-500">Added By:</label>
                                <span className="text-xs font-medium text-gray-900 dark:text-white">{entry.username}</span>
                              </div>
                            </div>
                            {/* Editable Category */}
                            <div>
                              <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Category:</label>
                              <select
                                name="category"
                                value={editFormData.category}
                                onChange={handleEditFormChange}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                              >
                                {categories.map((category) => (
                                  <option key={category} value={category}>{category}</option>
                                ))}
                              </select>
                            </div>
                            {/* Editable Type */}
                            <div>
                              <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Type:</label>
                              <select
                                name="type"
                                value={editFormData.type}
                                onChange={handleEditFormChange}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                              >
                                <option value="incoming">💰 Incoming</option>
                                <option value="outgoing">💸 Outgoing</option>
                              </select>
                            </div>
                            {/* Editable Amount */}
                            <div>
                              <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Amount:</label>
                              <div className="relative">
                                <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">Rs.</span>
                                <input
                                  type="number"
                                  name="amount"
                                  value={editFormData.amount}
                                  onChange={handleEditFormChange}
                                  placeholder="0.00"
                                  className="w-full pl-8 pr-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                  required
                                  step="0.01"
                                  min="0"
                                />
                              </div>
                            </div>
                            {/* Editable Description */}
                            <div className="md:col-span-2 lg:col-span-1">
                              <label className="block text-xs font-medium mb-1 text-gray-700 dark:text-gray-300">Description:</label>
                              <input
                                type="text"
                                name="description"
                                value={editFormData.description}
                                onChange={handleEditFormChange}
                                placeholder="Description"
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                required
                              />
                            </div>
                          </div>
                          <div className="flex justify-end space-x-2 pt-2">
                            <button
                              type="button"
                              onClick={handleCancelEdit}
                              className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-xs"
                            >
                              <FaTimes className="mr-1 inline h-3 w-3" />
                              Cancel
                            </button>
                            <button
                              type="submit"
                              className="px-3 py-1.5 bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-105 text-xs"
                            >
                              <FaSave className="mr-1 inline h-3 w-3" />
                              Save Changes
                            </button>
                          </div>
                        </form>
                      </div>
                    </td>
                  ) : (
                    <>
                      {/* Date Column */}
                      <td className="px-4 py-3 text-xs">
                        <div className="flex items-center">
                          <FaCalendar className="h-3 w-3 text-gray-400 mr-1" />
                          <span className="font-medium">{formatDate(entry.created_at)}</span>
                        </div>
                      </td>
                      {/* Category Column */}
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-white text-xs font-medium ${getCategoryBgColor(
                            entry.category
                          )}`}
                        >
                          <FaTag className="h-2 w-2 mr-1" />
                          {entry.category}
                        </span>
                      </td>
                      {/* Type Column */}
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            entry.type === 'incoming'
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          }`}
                        >
                          {entry.type === 'incoming' ? '💰 Incoming' : '💸 Outgoing'}
                        </span>
                      </td>
                      {/* Amount Column */}
                      <td className="px-4 py-3">
                        <span className={`font-bold text-sm ${
                          entry.type === 'incoming'
                            ? 'text-green-600 dark:text-green-400'
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          Rs.{parseFloat(entry.amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>
                      {/* Description Column */}
                      <td className="px-4 py-3">
                        <div className="max-w-xs">
                          <p className="text-xs text-gray-900 dark:text-white truncate" title={entry.description}>
                            {entry.description}
                          </p>
                        </div>
                      </td>
                      {/* Added By Column */}
                      <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400">
                        <div className="flex items-center">
                          <FaUser className="h-3 w-3 text-gray-400 mr-1" />
                          {entry.username}
                        </div>
                      </td>
                      {isAdmin && (
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center space-x-1">
                            <button
                              onClick={() => handleEditClick(entry)}
                              className="p-1.5 text-blue-500 hover:text-blue-700 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-all duration-200 transform hover:scale-110"
                              title="Edit entry"
                            >
                              <FaEdit className="h-3 w-3" />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(entry)}
                              className="p-1.5 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200 transform hover:scale-110"
                              title="Delete entry"
                            >
                              <FaTrash className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default EntryList;
